import React, { useEffect, useState } from "react";
import { Checkbox, Dropdown, PrimaryButton } from "@fluentui/react";
import {
  getAiDraftSiteSettings,
  getAiReportProfiles,
  saveAiDraftSiteSettings,
} from "../../../../service/AiDraftService";

const DEFAULT_PROFILE = "";

/**
 * Super-admin block in Hospital Settings: switch the AI Draft Report on for this hospital, choose
 * its AI service profile (house style + templates). The models radiologists can pick come from
 * ai.report.models. Saved on its own, independently of the firm form, to ai_report_site_profiles.
 */
const AiDraftSiteSettings = ({ firmId, readOnly }) => {
  const [enabled, setEnabled] = useState(false);
  const [profileId, setProfileId] = useState(DEFAULT_PROFILE);
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!firmId) return;
    let cancelled = false;
    (async () => {
      const [site, list] = await Promise.all([getAiDraftSiteSettings(firmId), getAiReportProfiles()]);
      if (cancelled) return;
      if (site?.status === 200) {
        setEnabled(!!site.data?.data?.enabled);
        setProfileId(site.data?.data?.profileId || DEFAULT_PROFILE);
      }
      if (list?.status === 200 && Array.isArray(list.data?.data)) setProfiles(list.data.data);
      else setProfilesError(list?.data?.message || "AI report service unavailable");
    })();
    return () => {
      cancelled = true;
    };
  }, [firmId]);

  const options = [
    { key: DEFAULT_PROFILE, text: "AI service default" },
    ...profiles.map((p) => ({
      key: p.profile_id,
      text: p.name ? `${p.name} (${p.profile_id})` : p.profile_id,
    })),
  ];
  // keep a saved profile visible even if the AI service no longer lists it
  if (profileId && !options.some((o) => o.key === profileId))
    options.push({ key: profileId, text: `${profileId} (not on AI service)` });

  const save = async () => {
    setSaving(true);
    setStatus(null);
    const response = await saveAiDraftSiteSettings(firmId, { enabled, profileId });
    setSaving(false);
    setStatus(response?.status === 200 ? "Saved" : response?.data?.message || "Could not save");
  };

  return (
    <div data-testid="ai-draft-site-settings">
      <div style={{ display: "flex", alignItems: "center", minHeight: "40px" }}>
        <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl4">
          <p className="single-line-form-field-key">Enable AI draft report</p>
        </div>
        <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
          <Checkbox
            checked={enabled}
            disabled={readOnly}
            ariaLabel="Enable AI draft report"
            onChange={(_, checked) => setEnabled(!!checked)}
          />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", minHeight: "40px" }}>
        <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl4">
          <p className="single-line-form-field-key">AI report profile</p>
        </div>
        <div className="ms-Grid-col ms-sm12 ms-lg8 ms-xl6 single-line-form-field-value">
          <Dropdown
            selectedKey={profileId}
            options={options}
            disabled={readOnly}
            ariaLabel="AI report profile"
            onChange={(_, option) => setProfileId(option ? String(option.key) : DEFAULT_PROFILE)}
            styles={{ root: { maxWidth: 320 } }}
          />
          {profilesError && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>{profilesError}</div>}
        </div>
      </div>
      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 12px" }}>
          <div className="ms-Grid-col ms-sm12 ms-lg4 ms-xl4" />
          <PrimaryButton text="Save AI draft settings" onClick={save} disabled={saving} />
          {status && <span style={{ fontSize: 12 }}>{status}</span>}
        </div>
      )}
    </div>
  );
};

export default AiDraftSiteSettings;
